// snap-points.ts — compute snap points + edge projection from extracted geometry

export interface SnapPoint {
  id: string
  label: string
  category: 'corner' | 'center' | 'edge' | 'hole'
  position: { x: number; y: number; z: number }
}

interface Envelope {
  min_x: number
  min_y: number
  max_x: number
  max_y: number
}

interface Feature {
  name: string
  type: string
  geometry: {
    shape: string
    diameter?: number
    locations?: Array<{ x: number; y: number }>
    center_x?: number
    center_y?: number
    [key: string]: unknown
  }
}

/**
 * Compute all snap points from extracted geometry.
 * Z=0 in CNC coordinates = top of stock (universal CNC convention).
 */
export function computeSnapPoints(geometry: Record<string, unknown>): SnapPoint[] {
  const env = geometry.part_envelope as Envelope | undefined
  if (!env) return []

  const features = (geometry.features ?? []) as Feature[]
  const points: SnapPoint[] = []

  // 4 top-face corners
  points.push({
    id: 'corner-front-left',
    label: 'Front-Left Corner',
    category: 'corner',
    position: { x: env.min_x, y: env.min_y, z: 0 },
  })
  points.push({
    id: 'corner-front-right',
    label: 'Front-Right Corner',
    category: 'corner',
    position: { x: env.max_x, y: env.min_y, z: 0 },
  })
  points.push({
    id: 'corner-back-left',
    label: 'Back-Left Corner',
    category: 'corner',
    position: { x: env.min_x, y: env.max_y, z: 0 },
  })
  points.push({
    id: 'corner-back-right',
    label: 'Back-Right Corner',
    category: 'corner',
    position: { x: env.max_x, y: env.max_y, z: 0 },
  })

  // Stock center
  points.push({
    id: 'center',
    label: 'Center',
    category: 'center',
    position: {
      x: (env.min_x + env.max_x) / 2,
      y: (env.min_y + env.max_y) / 2,
      z: 0,
    },
  })

  // 4 edge midpoints
  const midX = (env.min_x + env.max_x) / 2
  const midY = (env.min_y + env.max_y) / 2
  points.push({
    id: 'edge-front',
    label: 'Front Edge Mid',
    category: 'edge',
    position: { x: midX, y: env.min_y, z: 0 },
  })
  points.push({
    id: 'edge-back',
    label: 'Back Edge Mid',
    category: 'edge',
    position: { x: midX, y: env.max_y, z: 0 },
  })
  points.push({
    id: 'edge-left',
    label: 'Left Edge Mid',
    category: 'edge',
    position: { x: env.min_x, y: midY, z: 0 },
  })
  points.push({
    id: 'edge-right',
    label: 'Right Edge Mid',
    category: 'edge',
    position: { x: env.max_x, y: midY, z: 0 },
  })

  // Hole centers from features
  let holeIdx = 0
  for (const feature of features) {
    const g = feature.geometry
    if (g.shape !== 'cylinder') continue
    if (!['hole', 'bore', 'thread'].includes(feature.type)) continue

    const diam = g.diameter
    const label = diam ? `\u2300${diam}mm ${feature.type === 'thread' ? 'Thread' : 'Hole'}` : feature.name
    const locs = g.locations ?? (g.center_x != null ? [{ x: g.center_x!, y: g.center_y! }] : [])

    for (const loc of locs) {
      points.push({
        id: `hole-${holeIdx}`,
        label,
        category: 'hole',
        position: { x: loc.x, y: loc.y, z: 0 },
      })
      holeIdx++
    }
  }

  return points
}

/**
 * The 4 edges of the top face from the part envelope.
 * Each edge is [startX, startY] -> [endX, endY] in CNC coords.
 */
function topFaceEdges(env: Envelope): Array<{ id: string; x1: number; y1: number; x2: number; y2: number }> {
  return [
    { id: 'edge-front',  x1: env.min_x, y1: env.min_y, x2: env.max_x, y2: env.min_y },
    { id: 'edge-back',   x1: env.min_x, y1: env.max_y, x2: env.max_x, y2: env.max_y },
    { id: 'edge-left',   x1: env.min_x, y1: env.min_y, x2: env.min_x, y2: env.max_y },
    { id: 'edge-right',  x1: env.max_x, y1: env.min_y, x2: env.max_x, y2: env.max_y },
  ]
}

/**
 * Project a point onto the nearest top-face edge.
 * Returns the projected position (clamped to edge endpoints) if within `threshold` mm.
 */
export function projectToNearestEdge(
  point: { x: number; y: number },
  envelope: Envelope,
  threshold: number,
): { x: number; y: number; edgeId: string } | null {
  const edges = topFaceEdges(envelope)
  let bestDist = Infinity
  let bestResult: { x: number; y: number; edgeId: string } | null = null

  for (const edge of edges) {
    const dx = edge.x2 - edge.x1
    const dy = edge.y2 - edge.y1
    const lenSq = dx * dx + dy * dy
    if (lenSq === 0) continue

    // Parameter t along edge [0,1]
    let t = ((point.x - edge.x1) * dx + (point.y - edge.y1) * dy) / lenSq
    t = Math.max(0, Math.min(1, t))

    const projX = edge.x1 + t * dx
    const projY = edge.y1 + t * dy
    const dist = Math.sqrt((point.x - projX) ** 2 + (point.y - projY) ** 2)

    if (dist < bestDist) {
      bestDist = dist
      bestResult = { x: projX, y: projY, edgeId: edge.id }
    }
  }

  if (bestResult && bestDist <= threshold) return bestResult
  return null
}

export type SnapCategory = 'corner' | 'center' | 'edge' | 'hole' | 'free'

export interface SnapResolution {
  /** Resolved CNC position (snapped to a feature, or the raw point if nothing is in range). */
  position: { x: number; y: number; z: number }
  category: SnapCategory
  /** Human-friendly description, e.g. "corner", "hole center", "free point". */
  label: string
  /** Id of the snap point we locked onto, or null for an edge projection / free point. */
  snapId: string | null
}

const CATEGORY_LABEL: Record<SnapCategory, string> = {
  corner: 'corner',
  center: 'center',
  edge: 'edge',
  hole: 'hole center',
  free: 'free point',
}

/**
 * Resolve a raw clicked CNC point to the nearest meaningful feature.
 *
 * Snaps to the closest snap point (corner / hole / center / edge midpoint) within
 * `threshold`. If no discrete snap point is in range, projects onto the nearest top-face
 * edge. If nothing at all is close, returns the raw point as a "free point" so the user
 * can place an origin anywhere — i.e. snap only when there's a "bob" to snap to.
 */
export function resolveSnapPoint(
  rawCnc: { x: number; y: number; z: number },
  snapPoints: SnapPoint[],
  envelope: Envelope | undefined,
  threshold: number,
): SnapResolution {
  let best: SnapPoint | null = null
  let bestDist = Infinity
  for (const sp of snapPoints) {
    const dx = sp.position.x - rawCnc.x
    const dy = sp.position.y - rawCnc.y
    const dist = Math.sqrt(dx * dx + dy * dy)
    if (dist < bestDist) {
      bestDist = dist
      best = sp
    }
  }

  if (best && bestDist <= threshold) {
    return {
      position: best.position,
      category: best.category,
      label: CATEGORY_LABEL[best.category],
      snapId: best.id,
    }
  }

  if (envelope) {
    const proj = projectToNearestEdge(rawCnc, envelope, threshold)
    if (proj) {
      return {
        position: { x: proj.x, y: proj.y, z: 0 },
        category: 'edge',
        label: CATEGORY_LABEL.edge,
        snapId: null,
      }
    }
  }

  return {
    position: rawCnc,
    category: 'free',
    label: CATEGORY_LABEL.free,
    snapId: null,
  }
}

/** Convert CNC coordinates {x, y, z} to R3F scene coordinates [x, y, z]. */
export function cncToScene(
  cnc: { x: number; y: number; z: number },
  stockZ: number,
): [number, number, number] {
  // Scene: [cnc_x, stock_z + cnc_z, -cnc_y]
  // CNC Z=0 is top of stock → scene Y = stockZ
  return [cnc.x, stockZ + cnc.z, -cnc.y]
}

/** Convert R3F scene intersection point to CNC coordinates. */
export function sceneToCnc(
  scenePoint: { x: number; y: number; z: number },
  stockZ: number,
): { x: number; y: number; z: number } {
  return {
    x: scenePoint.x,
    y: -scenePoint.z,
    z: scenePoint.y - stockZ,
  }
}
